import type { GoalExplosionSelection, GoalExplosionSelectionState } from '../game/effects/GoalExplosionSelection';
import { validateGoalExplosionDefinition } from '../game/effects/MatchEffectsDefinition';
import type { Team, MatchTeamSize, MatchResult } from '../game/types';
import { parseMusicSelection } from '../game/audio/MusicSelection';
import type { MusicSelection } from '../music/replay/MusicContracts';
import type { GameContentSnapshot } from '../game/content/GameContentContracts';
import { parseChatMessage } from '../chat/ChatProtocol';
import { CHAT_HISTORY_LIMIT, type ChatMessage } from '../chat/ChatTypes';

export const ROOM_PROTOCOL_VERSION = 12;
export const RECONNECT_WINDOW_MS = 15_000;
export const ROOM_HANDSHAKE_TIMEOUT_MS = 15_000;
export const MAX_ROOM_CLIENT_CONTROL_BYTES = 16 * 1024;
export const MAX_ROOM_CONTROL_BYTES = 64 * 1024;

export interface RoomSettings {
  readonly kind: 'matchmaking' | 'custom';
  readonly teamSize: MatchTeamSize;
  readonly name: string;
  readonly access: 'open' | 'password';
  readonly playerAdmission: 'open' | 'closed';
  readonly spectatorAdmission: 'open' | 'closed';
  readonly mode: 'casual' | 'ranked' | 'permissive';
  readonly durationSeconds: number | null;
  readonly boostMode: 'normal' | 'unlimited';
}

export interface CreateRoomRequest extends Pick<RoomSettings, 'teamSize' | 'playerAdmission' | 'spectatorAdmission'> {
  readonly name?: string;
  readonly password?: string;
  readonly durationSeconds?: number | null;
  readonly boostMode?: 'normal' | 'unlimited';
  readonly bots: readonly { readonly botId: string; readonly team: Team }[];
}

export interface RoomParticipant {
  readonly id: string;
  readonly displayName: string;
  readonly kind: 'guest' | 'account' | 'bot';
  readonly role: 'player' | 'spectator';
  readonly entityId: number | null;
  readonly team: Team | null;
  readonly botId?: string;
  readonly lobbyReady: boolean;
  readonly slotId?: string | null;
  readonly controllerEpoch?: number;
  readonly status: 'connecting' | 'active' | 'loaded' | 'reconnecting';
}

export interface RoomSlot {
  readonly id: string;
  readonly team: Team;
  readonly entityId: number;
  readonly occupantId: string | null;
  readonly status: 'searching' | 'vacant' | 'entering' | 'loaded' | 'reconnecting';
}

export type RoomResult = MatchResult;

export interface RoomDescription {
  readonly id: string;
  readonly code: string;
  readonly settings: RoomSettings;
  readonly ownerId: string;
  readonly ownerStartEligible: boolean;
  readonly phase: 'lobby' | 'preparing' | 'running' | 'finished';
  readonly preparationRevision: number;
  readonly slots: readonly RoomSlot[];
  readonly result: RoomResult | null;
  readonly resultConfirmation: 'none' | 'pending' | 'confirmed';
  readonly preparationDeadlineAt: number | null;
  readonly forfeitAfterPlayedSeconds: number;
  readonly participants: readonly RoomParticipant[];
  readonly snapshot: GameContentSnapshot;
}

export interface RoomWelcome {
  readonly type: 'welcome';
  readonly version: typeof ROOM_PROTOCOL_VERSION;
  readonly room: RoomDescription;
  readonly participantId: string;
  readonly resumeToken: string;
  readonly localEntityId: number | null;
  readonly connectionGeneration: number;
}

export type RoomOperation =
  | { readonly type: 'recover'; readonly roomId: string; readonly authorization: string }
  | { readonly type: 'create'; readonly config: CreateRoomRequest; readonly allocationId?: string }
  | { readonly type: 'admit'; readonly roomId: string; readonly reservationId: string }
  | { readonly type: 'join'; readonly code: string; readonly password?: string }
  | { readonly type: 'resume'; readonly roomId: string; readonly participantId: string; readonly resumeToken: string };

export type RoomCommand =
  | { readonly type: 'authenticate'; readonly version: number; readonly token: string; readonly operation: RoomOperation }
  | { readonly type: 'start' }
  | { readonly type: 'client-ready'; readonly preparationRevision: number; readonly connectionGeneration: number }
  | { readonly type: 'lobby-ready'; readonly ready: boolean }
  | { readonly type: 'select-team'; readonly team: Team | null }
  | { readonly type: 'kick'; readonly participantId: string }
  | { readonly type: 'transfer-owner'; readonly participantId: string }
  | { readonly type: 'close-room' }
  | { readonly type: 'forfeit-vote' }
  | { readonly type: 'request-replacement'; readonly slotId: string }
  | { readonly type: 'leave' }
  | { readonly type: 'chat-send'; readonly clientMessageId: string; readonly text: string }
  | { readonly type: 'ping' };

export interface RoomMusicState {
  readonly type: 'music-state';
  readonly next: MusicSelection | null;
  readonly current: { readonly goalTick: number; readonly selection: MusicSelection | null } | null;
}

export function parseRoomMusicState(value: unknown): RoomMusicState {
  if (!value || typeof value !== 'object') throw new Error('Invalid room music state.');
  const state = value as RoomMusicState;
  if (state.type !== 'music-state' || (state.current !== null && (!state.current
    || !Number.isSafeInteger(state.current.goalTick) || state.current.goalTick < 0))) {
    throw new Error('Invalid room music binding.');
  }
  return { type: 'music-state', next: parseMusicSelection(state.next), current: state.current === null
    ? null : { goalTick: state.current.goalTick, selection: parseMusicSelection(state.current.selection) } };
}

export type RoomEventParams = Readonly<Record<string, string | number | boolean>>;

export const ROOM_SYSTEM_ANNOUNCEMENT_CODES = [
  'participant-joined',
  'participant-connection-lost',
  'participant-reconnected',
  'participant-left',
  'participant-join-failed',
  'bot-joined',
  'bot-reconnected',
  'bot-unavailable',
] as const;

export type RoomSystemAnnouncementCode = typeof ROOM_SYSTEM_ANNOUNCEMENT_CODES[number];

export function isRoomSystemAnnouncementCode(value: unknown): value is RoomSystemAnnouncementCode {
  return typeof value === 'string'
    && (ROOM_SYSTEM_ANNOUNCEMENT_CODES as readonly string[]).includes(value);
}

export interface RoomSystemAnnouncement {
  readonly type: 'system-announcement';
  readonly id: string;
  readonly sequence: number;
  readonly channelId: string;
  readonly createdAt: number;
  readonly code: RoomSystemAnnouncementCode;
  readonly params?: RoomEventParams;
}

export type RoomCloseCode =
  | 'entry-timeout'
  | 'simulation-start-failed'
  | 'invalid-reservation'
  | 'incomplete-teams'
  | 'lease-expired'
  | 'lease-unavailable'
  | 'match-start-failed'
  | 'match-cancelled'
  | 'server-shutdown'
  | 'kicked'
  | 'owner-closed'
  | 'empty-room'
  | 'simulation-ended'
  | 'match-ended';

export interface RoomChatMessage {
  readonly message: ChatMessage;
  readonly authorTeam: Team | null;
}

export type RoomChatEvent =
  | { readonly type: 'chat-history'; readonly messages: readonly RoomChatMessage[]; readonly canSend: boolean }
  | { readonly type: 'chat-message'; readonly message: ChatMessage; readonly authorTeam: Team | null };

type SharedRoomEvent = RoomWelcome
  | RoomMusicState
  | GoalExplosionSelectionState
  | { readonly type: 'roster'; readonly room: RoomDescription }
  | { readonly type: 'replay-votes'; readonly pendingNames: readonly string[] }
  | { readonly type: 'forfeit-votes'; readonly team: Team; readonly pendingNames: readonly string[];
      readonly voterParticipantIds: readonly string[]; readonly expiresAt: number }
  | { readonly type: 'latency'; readonly samples: readonly {
      readonly participantId: string; readonly valueMs: number | null; readonly source: 'remote' | 'local';
    }[] }
  | { readonly type: 'takeover'; readonly slotId: string; readonly entityId: number;
      readonly controllerEpoch: number; readonly spectatorSeconds: number }
  | { readonly type: 'pong' }
  | RoomChatEvent
  | { readonly type: 'chat-ack'; readonly clientMessageId: string }
  | { readonly type: 'chat-policy'; readonly canSend: boolean };

export type RoomWireEvent = SharedRoomEvent
  | { readonly type: 'error'; readonly code: string; readonly params?: RoomEventParams;
      readonly fatal: boolean; readonly clientMessageId?: string }
  | { readonly type: 'closed'; readonly code: RoomCloseCode; readonly params?: RoomEventParams }
  | RoomSystemAnnouncement;

export type RoomEvent = SharedRoomEvent
  | { readonly type: 'error'; readonly code: string; readonly params?: RoomEventParams;
      readonly message: string; readonly fatal: boolean; readonly clientMessageId?: string }
  | { readonly type: 'closed'; readonly code: RoomCloseCode; readonly params?: RoomEventParams; readonly message: string }
  | RoomSystemAnnouncement;

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });

export function encodeRoomControl(value: RoomCommand | RoomWireEvent): ArrayBuffer {
  const bytes = encoder.encode(JSON.stringify(value));
  if (bytes.byteLength > MAX_ROOM_CONTROL_BYTES) throw new Error('Room control packet is too large.');
  return bytes.buffer as ArrayBuffer;
}

export function decodeRoomControl(payload: ArrayBuffer): Record<string, unknown> | null {
  if (!payload.byteLength || payload.byteLength > MAX_ROOM_CONTROL_BYTES || new Uint8Array(payload)[0] !== 123) return null;
  try {
    const result: unknown = JSON.parse(decoder.decode(payload));
    if (!result || typeof result !== 'object' || Array.isArray(result)) return null;
    return result as Record<string, unknown>;
  } catch { return null; }
}

export function parseRoomChatEvent(value: unknown): RoomChatEvent {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid room chat event.');
  const event = value as Record<string, unknown>;
  const authorTeam = (team: unknown): Team | null => {
    if (team === null || team === 'blue' || team === 'orange') return team;
    throw new Error('Invalid room chat team.');
  };
  if (event.type === 'chat-message') {
    return { type: 'chat-message', message: parseChatMessage(event.message), authorTeam: authorTeam(event.authorTeam) };
  }
  if (event.type === 'chat-history') {
    if (typeof event.canSend !== 'boolean' || !Array.isArray(event.messages) || event.messages.length > CHAT_HISTORY_LIMIT) {
      throw new Error('Invalid room chat history.');
    }
    return {
      type: 'chat-history',
      canSend: event.canSend,
      messages: event.messages.map((entry) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) throw new Error('Invalid room chat history entry.');
        const item = entry as Record<string, unknown>;
        return { message: parseChatMessage(item.message), authorTeam: authorTeam(item.authorTeam) };
      }),
    };
  }
  throw new Error('Unknown room chat event.');
}

export function parseRoomGoalExplosionState(value: unknown): GoalExplosionSelectionState {
  if (!value || typeof value !== 'object') throw new Error('Invalid explosion state.');
  const state = value as GoalExplosionSelectionState;
  if (state.type !== 'explosion-state' || !Array.isArray(state.entities) || state.entities.length > 6) {
    throw new Error('Invalid explosion roster.');
  }
  const validId = (id: number) => Number.isSafeInteger(id) && id > 0;
  const selection = (entry: GoalExplosionSelection | null): GoalExplosionSelection | null => {
    if (entry === null) return null;
    if (!entry || typeof entry.itemId !== 'string' || !entry.itemId.trim() || entry.itemId.length > 128
      || !Number.isSafeInteger(entry.revision) || entry.revision < 0) throw new Error('Invalid explosion selection.');
    validateGoalExplosionDefinition(entry.definition);
    return entry;
  };
  const ids = new Set<number>();
  for (const entry of state.entities) {
    if (!entry || !validId(entry.entityId) || ids.has(entry.entityId)) throw new Error('Invalid explosion entity.');
    ids.add(entry.entityId);
    selection(entry.current); selection(entry.next);
  }
  if (state.goal !== null) {
    if (!state.goal || !Number.isSafeInteger(state.goal.goalTick) || state.goal.goalTick < 0
      || state.goal.scorerEntityId !== null && !validId(state.goal.scorerEntityId)) throw new Error('Invalid explosion binding.');
    selection(state.goal.selection);
  }
  return state;
}
